import {
  TransferDestinationInput,
  TransferSourceInput,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useHeaderHeight, useIsFocused } from 'expo-router/react-navigation';
import {
  TransferDestinationOption,
  TransferSourceOption,
} from 'features/feeBasedInstantTransfer/adapters/transferOptionsAdapter';
import TransferAmountField from 'features/feeBasedInstantTransfer/components/TransferAmountField/TransferAmountField';
import TransferOptionSelector from 'features/feeBasedInstantTransfer/components/TransferOptionSelector/TransferOptionSelector';
import {
  clampAmountToRange,
  normalizeAmount,
} from 'features/feeBasedInstantTransfer/helpers/transferAmount';
import { evaluateTransferAmount } from 'features/feeBasedInstantTransfer/helpers/transferAmountRules';
import { getTransferBlockingError } from 'features/feeBasedInstantTransfer/helpers/transferBlockingError';
import { useSelectedRoutePolicy } from 'features/feeBasedInstantTransfer/hooks/useSelectedRoutePolicy';
import { useTransferOptions } from 'features/feeBasedInstantTransfer/hooks/useTransferOptions';
import { useTransferPreselection } from 'features/feeBasedInstantTransfer/hooks/useTransferPreselection';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, ScrollView, StyleSheet, View } from 'react-native';
import type { AmountInputRef } from 'shared/components/vxl/AmountInput/AmountInput';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { isDesktopBrowser, isWeb } from 'shared/constants/platform';
import KeyboardAvoidingContainer from 'shared/containers/KeyboardAvoidingContainer/KeyboardAvoidingContainer';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const DEFAULT_AMOUNT = '0.00';
const TRANSFER_CURRENCY = 'USD';
const BLOCKING_ERROR_MESSAGE_KEYS = {
  source: 'sourceUnavailable',
  destination: 'destinationUnavailable',
  limits: 'limitsUnavailable',
} as const;

function FeeBasedInstantTransferEntryScreen() {
  const router = useRouter();
  const { flowName } = useLocalSearchParams<{ flowName?: string }>();
  const isFocused = useIsFocused();
  const headerHeight = useHeaderHeight();
  const amountInputRef = useRef<AmountInputRef>(null);
  const hasAttemptedAmountFocus = useRef(false);
  const { t: translatedText } = useTranslation('feeBasedInstantTransfer', {
    keyPrefix: 'entry',
  });
  const [source, setSource] = useState<TransferSourceInput>();
  const [destination, setDestination] = useState<TransferDestinationInput>();
  const [hasUserEditedSelection, setHasUserEditedSelection] = useState(false);
  const [enteredAmount, setEnteredAmount] = useState<string>();
  const {
    sourceOptions,
    destinationOptions,
    isSourceLoading,
    isDestinationLoading,
    sourceIssue,
    destinationIssue,
    retrySourceOptions,
    retryDestinationOptions,
  } = useTransferOptions(source);
  const hasSourceError = sourceIssue !== undefined;
  const hasDestinationError = destinationIssue !== undefined;
  const {
    isTransferPreselectionPending,
    transferPreselectedOptions,
    isAccountInteractionDisallowed,
  } = useTransferPreselection({
    sourceOptions,
    destinationOptions,
    isSourceLoading,
    isDestinationLoading,
    hasSourceError,
    hasDestinationError,
    source,
  });
  const policy = useSelectedRoutePolicy({ source, destination });
  const normalizedTargetAmount =
    transferPreselectedOptions?.targetAmount === undefined
      ? undefined
      : normalizeAmount(transferPreselectedOptions.targetAmount);
  const clampedTargetAmount =
    normalizedTargetAmount !== undefined && policy.status === 'ready'
      ? clampAmountToRange(
          normalizedTargetAmount,
          policy.minimumPrincipal?.minorUnits,
          policy.maximumPrincipal?.minorUnits,
        )
      : undefined;
  if (enteredAmount === undefined && clampedTargetAmount !== undefined) {
    setEnteredAmount(clampedTargetAmount);
  }
  const amount =
    enteredAmount ??
    clampedTargetAmount ??
    (transferPreselectedOptions?.amount === undefined
      ? undefined
      : normalizeAmount(transferPreselectedOptions.amount)) ??
    DEFAULT_AMOUNT;
  const evaluation = evaluateTransferAmount({ amount, policy });
  const isTargetAmountPending =
    normalizedTargetAmount !== undefined &&
    enteredAmount === undefined &&
    policy.status === 'loading';
  const isPreselectionLoading =
    (isTransferPreselectionPending || isTargetAmountPending) &&
    !hasUserEditedSelection;
  const retryRoutePolicy =
    policy.status === 'unavailable' ? policy.onRetry : undefined;
  const hasNoSourceOptions =
    !isSourceLoading &&
    !hasSourceError &&
    !sourceOptions.some((option) => option.isSelectable);
  const blockingError = getTransferBlockingError({
    sourceIssue: hasNoSourceOptions
      ? { kind: 'blocked', message: translatedText('noEligibleSources') }
      : sourceIssue,
    destinationIssue,
    hasLimitsError: retryRoutePolicy !== undefined,
  });
  const selectedSource = sourceOptions.find(
    (option) => option.id === source?.id,
  );
  const selectedDestination = destinationOptions.find(
    (option) =>
      option.id === destination?.id && option.input.type === destination.type,
  );
  const isApplyingPreselection =
    !hasUserEditedSelection &&
    ((transferPreselectedOptions?.source !== undefined &&
      source === undefined) ||
      (transferPreselectedOptions?.destination !== undefined &&
        destination === undefined));
  const hasRequestedTransferFailed =
    !isPreselectionLoading &&
    !isApplyingPreselection &&
    (!selectedSource?.isSelectable ||
      !selectedDestination?.isSelectable ||
      policy.status === 'unavailable');
  const isAccountInteractionLocked =
    isAccountInteractionDisallowed &&
    !hasUserEditedSelection &&
    !hasRequestedTransferFailed;
  const reviewParams =
    evaluation.isValid &&
    policy.status === 'ready' &&
    !isPreselectionLoading &&
    source &&
    destination &&
    selectedSource?.isSelectable &&
    selectedDestination?.isSelectable
      ? {
          sourceAccountId: source.id,
          destinationId: destination.id,
          destinationType: destination.type,
          amount,
          currency: TRANSFER_CURRENCY,
          flowName,
        }
      : undefined;
  const isAmountFocusReady =
    !isSourceLoading &&
    !isDestinationLoading &&
    !isPreselectionLoading &&
    !isApplyingPreselection &&
    !blockingError &&
    policy.status !== 'loading' &&
    policy.status !== 'unavailable';
  const hasBlockingError = blockingError !== undefined;

  useEffect(() => {
    if (!isFocused) {
      if (!isWeb) {
        hasAttemptedAmountFocus.current = false;
      }
      amountInputRef.current?.blur();
      Keyboard.dismiss();
    }
  }, [isFocused]);

  useEffect(() => {
    if (
      isFocused &&
      isAmountFocusReady &&
      !hasAttemptedAmountFocus.current &&
      (!isWeb || isDesktopBrowser)
    ) {
      hasAttemptedAmountFocus.current = true;
      amountInputRef.current?.focus();
    }
  }, [isFocused, isAmountFocusReady]);

  useEffect(() => {
    if (isFocused && hasBlockingError) {
      amountInputRef.current?.blur();
      Keyboard.dismiss();
    }
  }, [hasBlockingError, isFocused]);

  useEffect(() => {
    if (!transferPreselectedOptions || hasUserEditedSelection) {
      return;
    }

    if (transferPreselectedOptions.source) {
      setSource(transferPreselectedOptions.source);
    }
    if (transferPreselectedOptions.destination) {
      setDestination(transferPreselectedOptions.destination);
    }
  }, [hasUserEditedSelection, transferPreselectedOptions]);

  const handleSourceSelect = (option: TransferSourceOption) => {
    setHasUserEditedSelection(true);
    setSource(option.input);
    setDestination(undefined);
  };

  const handleKeyboardDismiss = () => {
    hasAttemptedAmountFocus.current = true;
    amountInputRef.current?.blur();
    Keyboard.dismiss();
  };

  const handleAmountFocus = () => {
    hasAttemptedAmountFocus.current = true;
  };

  const handleDestinationSelect = (option: TransferDestinationOption) => {
    setHasUserEditedSelection(true);
    setDestination(option.input);
  };

  const handleBlockingErrorRetry = () => {
    if (blockingError?.kind !== 'retry') {
      return;
    }
    switch (blockingError.target) {
      case 'source':
        retrySourceOptions();
        break;
      case 'destination':
        retryDestinationOptions();
        break;
      case 'limits':
        setHasUserEditedSelection(true);
        retryRoutePolicy?.();
        break;
    }
  };

  const handleGoBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/');
    }
  };

  const getBlockingErrorScreen = () => {
    if (blockingError?.kind === 'retry') {
      return {
        headline: translatedText('errorTitle'),
        body: translatedText(BLOCKING_ERROR_MESSAGE_KEYS[blockingError.target]),
        primaryButton: {
          title: translatedText('retry'),
          onPress: handleBlockingErrorRetry,
        },
      };
    }
    if (blockingError?.kind === 'blocked') {
      return {
        headline: translatedText('transferUnavailableTitle'),
        body:
          blockingError.message || translatedText('transferUnavailableMessage'),
        primaryButton: {
          title: translatedText('goBack'),
          onPress: handleGoBack,
        },
      };
    }
    return undefined;
  };
  const blockingErrorScreen = getBlockingErrorScreen();

  const handleReviewPress = () => {
    if (reviewParams) {
      amountInputRef.current?.blur();
      Keyboard.dismiss();
      router.push({
        pathname: '/move-money/transfer/review-transfer-details',
        params: reviewParams,
      });
    }
  };

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        ErrorComponent={
          blockingErrorScreen ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={blockingErrorScreen.headline}
              messageBody={blockingErrorScreen.body}
              primaryButton={blockingErrorScreen.primaryButton}
            />
          ) : null
        }
        isLoading={isPreselectionLoading}
        LoadingComponent={<LoadingSpinner />}
      />
      <KeyboardAvoidingContainer
        keyboardVerticalOffset={headerHeight}
        containerStyle={styles.keyboardContainer}
        contentContainerStyle={styles.contentContainer}
        actionsContainerStyle={styles.actionsContainer}
      >
        <KeyboardAvoidingContainer.Content>
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            testID="fee-based-instant-transfer-entry"
          >
            <TransferOptionSelector
              label={translatedText('source')}
              sheetTitle={translatedText('transferFrom')}
              options={sourceOptions}
              selectedOption={selectedSource}
              isReadOnly={isAccountInteractionLocked}
              isLoading={isSourceLoading}
              onSelect={handleSourceSelect}
              onOpen={handleKeyboardDismiss}
            />
            <TransferOptionSelector
              label={translatedText('destination')}
              sheetTitle={translatedText('transferTo')}
              options={destinationOptions}
              selectedOption={selectedDestination}
              isDisabled={!source}
              isReadOnly={isAccountInteractionLocked}
              isLoading={isDestinationLoading}
              onSelect={handleDestinationSelect}
              onOpen={handleKeyboardDismiss}
            />
            <View style={styles.amount}>
              <TransferAmountField
                ref={amountInputRef}
                isDisabled={!isFocused || hasBlockingError}
                onFocus={handleAmountFocus}
                amount={amount}
                policy={policy}
                error={evaluation.error}
                onAmountChange={setEnteredAmount}
              />
            </View>
            <View
              style={styles.background}
              onStartShouldSetResponder={() => true}
              onResponderRelease={handleKeyboardDismiss}
            />
          </ScrollView>
        </KeyboardAvoidingContainer.Content>
        <KeyboardAvoidingContainer.Actions>
          <Button
            isDisabled={!reviewParams}
            label={translatedText('reviewTransfer')}
            onPress={handleReviewPress}
          />
        </KeyboardAvoidingContainer.Actions>
      </KeyboardAvoidingContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  background: {
    flexGrow: 1,
  },
  amount: {
    alignItems: 'center',
    gap: theme.spacing.s,
    justifyContent: 'center',
    minHeight: 201,
    padding: theme.spacing.m,
  },
  keyboardContainer: {
    paddingHorizontal: 0,
    paddingTop: 0,
  },
  contentContainer: {
    gap: 0,
  },
  actionsContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
});

export default FeeBasedInstantTransferEntryScreen;
